import { cn } from '@/lib/utils'
import {
  BaseLink,
  Button,
  Chip,
  Container,
  Distance,
  Img,
  OpeningStatus,
  RatingStars,
  Richtext,
  Section,
  Text,
  Time,
  Title,
  TourInfo
} from '@/ui/components'
import { cva } from 'class-variance-authority'
import { memo } from 'react'
import { SectionCtaSingleType } from './type'

// TODO: Check image quality
// TODO: Variant: RestBarShop add distance
// TODO: Variant: Hotel add stars, price, distance

const ImageWrapper = ({
  children,
  link,
  className
}: {
  children: React.ReactNode
  link: string | undefined
  className?: string
}) => {
  if (link !== undefined) {
    return (
      <BaseLink nextLink={true} href={link} className={className}>
        {children}
      </BaseLink>
    )
  } else {
    return <div className={className}>{children}</div>
  }
}

const SectionCtaSingle = ({
  section,
  variant = 'content',
  body,
  title,
  subTitle,
  date,
  button,
  chipContent,
  image,
  stars,
  price,
  distance,
  coordinates,
  openingHours,
  openingStatus,
  tourInfo
}: SectionCtaSingleType) => {
  const headerVariants = cva(
    'pt-lg pr-padding-md pb-lg pl-padding-md -ml-padding-md',
    {
      variants: {
        variant: {
          content: 'bg-neutral-100 text-neutral-600',
          hotel: 'bg-neutral-100 text-neutral-600',
          restBarShop: 'bg-neutral-100 text-neutral-600',
          story: 'bg-accent-300 text-neutral-100',
          topEvent: 'bg-neutral-200 text-neutral-600',
          tour: 'bg-neutral-100 text-neutral-600'
        }
      },
      defaultVariants: {
        variant: 'content'
      }
    }
  )

  const titleVariants = cva('h2 line-clamp-3', {
    variants: {
      variant: {
        content: '',
        hotel: '',
        restBarShop: '',
        story: '',
        topEvent: 'text-accent-300',
        tour: ''
      }
    },
    defaultVariants: {
      variant: 'content'
    }
  })

  return (
    <Section data={section} className="s-section-cta-single">
      <Container className="pr-0 max-sm:pl-0">
        <div className="relative flex flex-col items-start">
          {image && (
            <ImageWrapper
              link={button?.href}
              className={cn(
                'lg:mb-2xl aspect-[1.7] w-full md:mb-[20vw]',
                button?.href && 'group relative overflow-hidden'
              )}
            >
              <Img
                data={image}
                crop={{ width: 1650, height: 1020 }}
                objectCover
                fill
                className={cn(
                  'h-full w-full',
                  button?.href && 'image-hover-effect'
                )}
              />
            </ImageWrapper>
          )}
          <div className="max-sm:pl-padding-md relative bottom-0 md:absolute md:max-w-[clamp(28rem,50%,38rem)]">
            <header className={headerVariants({ variant })}>
              <div className="absolute bottom-full flex">
                {chipContent && <Chip>{chipContent}</Chip>}
                {openingStatus && (
                  <OpeningStatus data={openingStatus} framed={false} size="m" />
                )}
                {openingHours && (
                  <Chip variant="openingHours">{openingHours}</Chip>
                )}
              </div>
              <Time date={date} />
              <Title as="h2" className={titleVariants({ variant })}>
                {title}
              </Title>
              <Title as="h3" className="textstyle-lg-regular mt-2xs">
                {subTitle}
              </Title>
              <div className="add-separator flex">
                {stars && (
                  <RatingStars stars={stars} className="textstyle-md-bold" />
                )}
                {price && <Text className="textstyle-md-bold">{price}</Text>}
                {distance && <Text>{distance}</Text>}
                <Distance coordinates={coordinates} />
              </div>
              <TourInfo
                distance={tourInfo?.distance}
                duration={tourInfo?.duration}
                altitudeUphill={tourInfo?.altitudeUphill}
                altitudeDownhill={tourInfo?.altitudeDownhill}
              />
              <Richtext className="mt-lg textstyle-md-regular" text={body} />
              {button?.href && (
                <Button
                  size="m"
                  icon="material-symbols:chevron-right"
                  iconPosition="right"
                  className="mt-lg"
                  href={button.href}
                  target={button.target}
                >
                  {button.label}
                </Button>
              )}
            </header>
          </div>
        </div>
      </Container>
    </Section>
  )
}

SectionCtaSingle.displayName = 'SectionCtaSingle'

export default memo(SectionCtaSingle)
